import type { WerseeIdentity, WerseeIdentityPatch } from './identity';

type Envelope<T> = { data?: T; error?: { code?: string; message?: string } };

export type IdAuthProvider = {
  id: 'google' | 'azure' | 'github' | 'passkey';
  label: string;
  mode: 'direct-id-token' | 'supabase-oauth' | 'disabled';
  enabled: boolean;
  clientId?: string;
};

export type IdUser = {
  id: string;
  email: string | null;
  email_verified: boolean;
  created_at: string;
  identities: Array<{ id: string; provider: string; created_at?: string; last_sign_in_at?: string }>;
};

export type IdFactor = { id: string; friendly_name?: string; status?: string; created_at?: string };

export type IdMe = {
  user: IdUser;
  identity: WerseeIdentity;
  assurance: { currentLevel: 'aal1' | 'aal2' | null; nextLevel: 'aal1' | 'aal2' | null };
  factors: { totp: IdFactor[]; phone: IdFactor[] };
};

export type OAuthGrant = {
  client: { id: string; name: string; uri?: string | null; logo_uri?: string | null };
  scopes: string[];
  granted_at: string;
};

export type IdEmailVerification = {
  email: string;
  purpose: 'signup' | 'recovery';
  status_topic: string;
};

let csrfToken: string | null = null;
let csrfPromise: Promise<string> | null = null;

const read = async <T>(response: Response) => {
  const payload = await response.json().catch(() => ({})) as Envelope<T>;
  if (!response.ok || payload.data === undefined) {
    const error = new Error(payload.error?.message || 'Wersee ID is temporarily unavailable.') as Error & { code?: string; status?: number };
    error.code = payload.error?.code;
    error.status = response.status;
    throw error;
  }
  return payload.data;
};

const getCsrf = async () => {
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = fetch('/api/auth/csrf', { credentials: 'include', cache: 'no-store' })
      .then((response) => read<{ csrf_token: string }>(response))
      .then((data) => {
        csrfToken = data.csrf_token;
        return data.csrf_token;
      })
      .finally(() => { csrfPromise = null; });
  }
  return csrfPromise;
};

const request = async <T>(path: string, init: RequestInit = {}) => {
  const method = (init.method || 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD'].includes(method)) headers.set('X-Wersee-CSRF', await getCsrf());
  const response = await fetch(`/api/auth${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
    cache: 'no-store',
  });
  if (response.status === 403) csrfToken = null;
  return read<T>(response);
};

export const idAuth = {
  providers: () => request<{ providers: IdAuthProvider[]; passkeys_feature_flag: boolean }>('/providers'),
  login: (input: { email: string; password: string; redirect?: string }) => request<{ user: IdUser; redirect: string }>('/login', { method: 'POST', body: JSON.stringify(input) }),
  continue: (redirect?: string) => request<{ user: IdUser; redirect: string }>('/continue', { method: 'POST', body: JSON.stringify({ redirect }) }),
  signup: (input: { email: string; password: string; display_name?: string; redirect?: string }) => request<{ user: IdUser | null; confirmation_required: boolean; redirect: string; verification: IdEmailVerification | null }>('/signup', { method: 'POST', body: JSON.stringify(input) }),
  recover: (email: string, redirect?: string) => request<{ accepted: boolean; redirect: string; verification: IdEmailVerification }>('/recover', { method: 'POST', body: JSON.stringify({ email, redirect }) }),
  verifyEmail: (input: { email: string; code: string; purpose: 'signup' | 'recovery'; status_topic?: string; redirect?: string }) => request<{ verified: boolean; redirect: string }>('/verify-email', { method: 'POST', body: JSON.stringify(input) }),
  resendEmailVerification: (input: { email: string; purpose: 'signup' | 'recovery'; redirect?: string }) => request<{ accepted: boolean; redirect: string; verification: IdEmailVerification }>('/verify-email/resend', { method: 'POST', body: JSON.stringify(input) }),
  resetPassword: (password: string) => request<{ updated: boolean; redirect: string }>('/reset-password', { method: 'POST', body: JSON.stringify({ password }) }),
  googleContext: () => request<{ nonce: string }>('/google/context', { method: 'POST', body: '{}' }),
  google: (credential: string, redirect?: string) => request<{ user: IdUser; redirect: string }>('/google', { method: 'POST', body: JSON.stringify({ credential, redirect }) }),
  oauthStart: (provider: 'google' | 'azure' | 'github', redirect?: string) => request<{ url: string }>('/oauth/start', { method: 'POST', body: JSON.stringify({ provider, redirect }) }),
  session: () => request<{ account: IdMe | null }>('/session'),
  me: () => request<IdMe>('/me'),
  updateProfile: (patch: WerseeIdentityPatch & { email?: string }) => request<{ identity: WerseeIdentity; email_change_pending: boolean }>('/profile', { method: 'PATCH', body: JSON.stringify(patch) }),
  avatarUpload: (mimeType: string, size: number) => request<{ path: string; token: string; signed_url: string; public_url: string }>('/avatar-upload', { method: 'POST', body: JSON.stringify({ mime_type: mimeType, size }) }),
  logout: (scope: 'local' | 'others' | 'global' = 'local') => request<{ signed_out: boolean }>('/logout', { method: 'POST', body: JSON.stringify({ scope }) }),
  mfa: () => request<{ factors: { totp: IdFactor[]; phone: IdFactor[] }; assurance: IdMe['assurance'] }>('/mfa'),
  mfaEnroll: (friendlyName: string) => request<{ factor_id: string; qr_code: string; friendly_name?: string }>('/mfa/enroll', { method: 'POST', body: JSON.stringify({ friendly_name: friendlyName }) }),
  mfaChallenge: (factorId: string) => request<{ factor_id: string; challenge_id: string; expires_at?: number }>('/mfa/challenge', { method: 'POST', body: JSON.stringify({ factor_id: factorId }) }),
  mfaVerify: (factorId: string, challengeId: string, code: string, redirect?: string) => request<{ verified: boolean; redirect: string }>('/mfa/verify', { method: 'POST', body: JSON.stringify({ factor_id: factorId, challenge_id: challengeId, code, redirect }) }),
  mfaRemove: (factorId: string) => request<{ removed: boolean }>(`/mfa/${encodeURIComponent(factorId)}`, { method: 'DELETE' }),
  apps: () => request<{ grants: OAuthGrant[] }>('/apps'),
  revokeApp: (clientId: string) => request<{ revoked: boolean }>(`/apps/${encodeURIComponent(clientId)}`, { method: 'DELETE' }),
  oauthConsent: (authorizationId: string) => request<Record<string, unknown>>(`/oauth/consent?authorization_id=${encodeURIComponent(authorizationId)}`),
  decideOAuthConsent: (authorizationId: string, decision: 'approve' | 'deny') => request<{ redirect_url: string }>('/oauth/consent', { method: 'POST', body: JSON.stringify({ authorization_id: authorizationId, decision }) }),
};

export const uploadIdentityAvatar = async (file: File) => {
  const signed = await idAuth.avatarUpload(file.type, file.size);
  const form = new FormData();
  form.append('cacheControl', '3600');
  form.append('', file);
  const response = await fetch(signed.signed_url, {
    method: 'PUT',
    headers: { 'x-upsert': 'false' },
    body: form,
  });
  if (!response.ok) throw new Error('The avatar upload could not be completed.');
  return signed.public_url;
};
